import { useState } from 'react'
import {
  HeaderChildrenContainer,
  Wrapper,
} from './components/display/commonStyledComponents'
import { Button, Check, EmptyState, NoDataIcon, PageHeader } from '@lmc/ui-kit'
import { Form as AntdForm } from 'antd'
import { generalConstants } from 'locale/general'
import { showToast } from 'common/utils'
import { NAMESPACE } from 'locale'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { incidentReportsConstants } from 'locale/incidentReports'
import {
  IncidentReportWithCase,
  IncidentReportUpdateCaseBody,
  UserRole,
} from '@lmc/ts-common'
import { useParams } from 'react-router-dom'
import useIncidentReportByLog from 'hooks/useIncidentReport/useIncidentReportByLog'
import { Spinner } from 'components/generic'
import useIncidentReport from 'hooks/useIncidentReport/useIncidentReport'
import dayjs from 'dayjs'
import IncidentCaseForm, {
  IncidentReportEditCaseForm,
} from './components/forms/IncidentCaseForm'
import CloseIncidentModal from './components/modals/CloseIncidentModal'
import { getCombinedTimeIso } from './utils'
import {
  AttachmentFieldType,
  getNewFilesFormData,
} from './components/UploadAttachmentsOnSaveInput'
import NavigationModal from 'components/NavigationModal/NavigationModal'
import IncidentTypeChangeModal from './components/modals/IncidentTypeChangeModal'
import { useRoleAccess } from 'components/AccessControl'

const IncidentReportEditCaseScreen = () => {
  const [form] = AntdForm.useForm()
  const { t } = useTranslation(NAMESPACE.INCIDENT_REPORTS)
  const [isDirty, setIsDirty] = useState(false)
  const [isUpdateLoading, setIsUpdateLoading] = useState(false)
  const navigate = useNavigate()
  const { id: incidentLogId } = useParams()
  const [isClosingIncident, setIsClosingIncident] = useState(false)
  const [isModalVisible, setIsModalVisible] = useState(false)

  const handleOpen = () => {
    setIsModalVisible(true)
  }

  const handleCancel = () => {
    setIsModalVisible(false)
  }

  const { incidentReport, isIncidentReportLoading } =
    useIncidentReportByLog<IncidentReportWithCase>({
      incidentLogId,
    })

  const {
    updateIncidentReportCase,
    uploadIncidentReportFiles,
    deleteIncidentReportFiles,
  } = useIncidentReport({
    incidentReportId: incidentReport?.id,
    incidentLogId,
    onUpdateSuccess: () => {
      showToast({
        title: t(incidentReportsConstants.CASE_UPDATED),
      })
    },
  })

  if (isIncidentReportLoading) return <Spinner />
  if (!incidentReport)
    return (
      <EmptyState
        icon={<NoDataIcon />}
        title={t(incidentReportsConstants.LOG_NOT_FOUND)}
      />
    )

  const initialValues: Partial<IncidentReportEditCaseForm<string>> = {
    ...incidentReport.case,
    attachments: incidentReport.attachments.map((attachmentUrl, index) => {
      return {
        name: `${t(incidentReportsConstants.FILE)} ${index + 1}`,
        isDeleted: false,
        isNew: false,
        url: attachmentUrl,
      }
    }),
    serviceUsersInvolved: incidentReport.case.serviceUsersInvolved.map(
      (serviceUser) => serviceUser.id,
    ),
    carersInvolved: incidentReport.case.carersInvolved?.map(
      (serviceUser) => serviceUser.id,
    ),
    severity: incidentReport.severity,
    log: {
      ...incidentReport.log,
      goals: incidentReport.log.goals?.map((goal) => goal.id) || [],
      dateLogged: dayjs(incidentReport.log.timeLogged),
      timeLogged: dayjs(incidentReport.log.timeLogged),
      item: incidentReport.log.item?.id,
    },
  }

  const onSave = async (values: IncidentReportEditCaseForm<string>) => {
    try {
      setIsUpdateLoading(true)
      setIsDirty(false)
      const incidentReportEditCaseBody: IncidentReportUpdateCaseBody = {
        severity: values.severity,
        serviceUsersInvolved: values.serviceUsersInvolved,
        otherPeopleInvolved: values.otherPeopleInvolved,
        otherWitnesses: values.otherWitnesses,
        carersInvolved: values.carersInvolved,
        physicalIntervention: values.physicalIntervention,
        restraints: values.restraints,
        chemicalRestraints: values.chemicalRestraints,
        residentInjuries: values.residentInjuries,
        carersInjuries: values.carersInjuries,
        otherPartiesInjuries: values.otherPartiesInjuries,
        damages: values.damages,
        prnMedication: values.prnMedication,
        serviceUserOpinion: values.serviceUserOpinion,
        incidentOtherInformation: values.incidentOtherInformation,
        debriefingNotes: values.debriefingNotes,
        rootCause: values.rootCause,
        preventativeActions: values.preventativeActions,
        otherNotes: values.otherNotes,
        log: {
          ...values.log,
          timeLogged: getCombinedTimeIso(
            values?.log?.dateLogged,
            values?.log?.timeLogged,
          ),
        },
        followUpActions: {
          ...values.followUpActions,
          // deletes id from result
          actions: values?.followUpActions?.actions?.map((action) => ({
            title: action.title,
            status: action.status,
          })),
        },
      }

      await updateIncidentReportCase(incidentReportEditCaseBody)

      const deletedIndexes = getDeletedAttachmentsIndexes(values.attachments)
      if (deletedIndexes.length > 0) {
        await deleteIncidentReportFiles({ deletedIndexes })
      }

      const formData = getNewFilesFormData(values.attachments)
      if (formData) {
        await uploadIncidentReportFiles(formData)
      }

      navigate(`../`, { relative: 'path' })
    } catch {
      showToast({
        title: t(generalConstants.ERROR, { ns: NAMESPACE.GENERAL }),
        type: 'error',
      })
    }

    setIsUpdateLoading(false)
  }

  const onFinish = (values: IncidentReportEditCaseForm<string>) => {
    if (values.log.item !== incidentReport.log.item?.id) {
      handleOpen()
    } else {
      onSave(values)
    }
  }

  const onValuesChange = (values: any) => {
    if (values) {
      setIsDirty(true)
    }
  }

  const hasRolePermissionToClose = useRoleAccess(
    [UserRole.careHomeAdmin],
    [
      {
        i: 'update',
        this: 'Incident',
      },
    ],
  )

  return (
    <>
      <PageHeader
        sticky
        title={t(incidentReportsConstants.EDITING_INCIDENT_CASE)}
        onBack={() => navigate('../..', { relative: 'path' })}
      >
        <HeaderChildrenContainer>
          <Button onClick={() => navigate('../', { relative: 'path' })}>
            {t(incidentReportsConstants.CANCEL)}
          </Button>
          <Button
            onClick={() => setIsClosingIncident(true)}
            disabledByPermission={!hasRolePermissionToClose}
          >
            {t(incidentReportsConstants.CLOSE_INCIDENT)}
          </Button>
          <Button
            variant="primary"
            type="submit"
            onClick={form.submit}
            isLoading={isUpdateLoading}
          >
            {t(incidentReportsConstants.SAVE)} <Check color="white" />
          </Button>
        </HeaderChildrenContainer>
      </PageHeader>
      <Wrapper>
        <IncidentCaseForm
          form={form}
          onFinish={onFinish}
          onValuesChange={onValuesChange}
          initialValues={initialValues}
          incidentReport={incidentReport}
        />
        <NavigationModal canNavigate={!isDirty} />
        <CloseIncidentModal
          open={isClosingIncident}
          setIsClosingIncident={setIsClosingIncident}
          incidentReportId={incidentReport.id}
          onSuccess={() => navigate(`../`, { relative: 'path' })}
          incidentCase={incidentReport.case}
          saveChanges={isDirty ? form.submit : undefined}
        />
        <IncidentTypeChangeModal
          open={isModalVisible}
          handleCancel={handleCancel}
          onConfirm={() => onSave(form.getFieldsValue())}
        />
      </Wrapper>
    </>
  )
}

export const getDeletedAttachmentsIndexes = (
  attachments: AttachmentFieldType[],
): number[] => {
  const deletedIndexAttachments = attachments.map((attachment, index) => {
    if (attachment.isDeleted === true) {
      return index
    }

    return undefined
  })

  const filteredIndexes: number[] = deletedIndexAttachments.filter(
    (attachment) => attachment !== undefined,
  ) as number[]

  return filteredIndexes
}

export default IncidentReportEditCaseScreen
